import { useGetConfig } from "./getConfig";
import { useGetActiveMunicipality, useGetMunicipalities } from "./getMunicipality";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toValidDate = (value?: string | null) => {
  if (!value) return null;

  const parsedDate = new Date(value);
  return Number.isFinite(parsedDate.getTime()) ? parsedDate : null;
};

const getDaysRemainingFromDate = (date?: Date | null) => {
  if (!date) return null;

  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / DAY_IN_MS));
};

const getPlanDurationDays = (
  subPeriodDays: Record<string, number | null> | undefined,
  plan?: string | null,
) => {
  if (!subPeriodDays || !plan) return null;
  const durationDays = subPeriodDays[plan];
  return typeof durationDays === "number" ? durationDays : null;
};

const getPlanEndsAt = (
  subPeriodDays: Record<string, number | null> | undefined,
  plan?: string | null,
  planStartedAt?: string | null,
) => {
  if (!planStartedAt || !plan) return null;

  const durationDays = getPlanDurationDays(subPeriodDays, plan);
  if (durationDays === null) return null;

  const endDate = new Date(planStartedAt);
  endDate.setHours(12, 0, 0, 0);
  endDate.setDate(endDate.getDate() + durationDays);
  return endDate;
};

export const useGetSubscription = () => {
  const municipalityId = useGetActiveMunicipality();
  const { data: municipalities = [] } = useGetMunicipalities();
  const { data: config } = useGetConfig();
  const activeMunicipality = municipalities.find((m) => m.id === municipalityId);
  const plan = activeMunicipality?.plan;
  const planStartedAt = activeMunicipality?.planStartedAt;
  const municipalityPlanEndsAt = activeMunicipality?.planEndsAt;
  const municipalityPlanEndsAtDate = toValidDate(municipalityPlanEndsAt);
  const isVipOrTrial = plan === "VIP" || plan === "TRIAL";

  const durationDays = getPlanDurationDays(config?.subPeriodDays, plan);
  const fallbackDaysRemaining =
    planStartedAt && typeof durationDays === "number"
      ? Math.max(
          0,
          durationDays -
            Math.floor((Date.now() - new Date(planStartedAt).getTime()) / DAY_IN_MS)
        )
      : null;

  const fallbackEndsAt = getPlanEndsAt(config?.subPeriodDays, plan, planStartedAt);
  const endsAt = isVipOrTrial && municipalityPlanEndsAtDate
    ? municipalityPlanEndsAtDate
    : fallbackEndsAt;
  const daysRemaining = isVipOrTrial && municipalityPlanEndsAtDate
    ? getDaysRemainingFromDate(municipalityPlanEndsAtDate)
    : fallbackDaysRemaining;

  return {
    plan,
    planStartedAt,
    endsAt,
    daysRemaining,
  };
};

export const useHasActiveSubscription = () => {
  const subscription = useGetSubscription();
  if (!subscription.plan || !subscription.planStartedAt) return false;

  const planStartedAtDate = toValidDate(subscription.planStartedAt);
  if (!planStartedAtDate || planStartedAtDate.getTime() > Date.now()) {
    return false;
  }

  if (subscription.plan === "VIP") {
    return subscription.daysRemaining === null || subscription.daysRemaining > 0;
  }

  return (subscription.daysRemaining ?? 0) > 0;
};
